import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { publicConfig } from '@/config/publicConfig'
import { getUserSelfQueryKey, type User, useGetUserSelf } from '@/hooks/v1'
import { qrRequest } from '@/services/qrPromo/client'
import {
  createQrPollBudget,
  isQrTradingLocked,
  loadParticipations,
  type QrParticipation,
  type QrReward,
  type QrRewardAccepted,
} from '@/services/qrPromo/model'
import { cancelQrRefreshes, requestQrReward } from '@/services/qrPromo/query'

export type QrEntry = QrParticipation & { reward?: QrReward; refreshing: boolean; error: boolean }
type QrContext = {
  entries: QrEntry[]
  loading: boolean
  error: boolean
  tradingLocked: boolean
  requesting: string | null
  recoveryRequired: string | null
  refresh: (id?: string) => Promise<QrReward | undefined>
  requestReward: (id: string) => Promise<void>
}
const QrPromoContext = createContext<QrContext | null>(null)
export function QrPromoProvider({ children }: PropsWithChildren) {
  const client = useQueryClient()
  const { data: self } = useGetUserSelf()
  const user = self?.data
  const tenant = user?.tenant_id
  const userId = user?.id
  const scope = `${tenant}:${userId}`
  // Async claim callbacks compare their captured scope with the currently rendered account.
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const [requesting, setRequesting] = useState<string | null>(null)
  const [recoveryRequired, setRecoveryRequired] = useState<string | null>(null)
  const inFlight = useRef(false)
  const associationKey = ['qrPromo', tenant, userId, 'participants'] as const
  const rewardKey = (id: string) => ['qrPromo', tenant, userId, 'reward', id] as const
  const enabled = Boolean(userId && tenant && tenant === publicConfig.api.tenantId)
  const participations = useQuery({
    queryKey: associationKey,
    queryFn: ({ signal }) => loadParticipations(tenant!, userId!, qrRequest, signal),
    enabled,
    retry: false,
    staleTime: 15_000,
  })
  const rows = participations.data ?? []
  const rewards = useQueries({
    queries: rows.map((row) => ({
      queryKey: rewardKey(row.challenge.id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        qrRequest<QrReward>(
          `/v1/trading_challenges/${encodeURIComponent(row.challenge.id)}/reward`,
          { signal },
        ),
      retry: false,
      staleTime: 5000,
    })),
  })
  const entries: QrEntry[] = rows.map((row, index) => ({
    ...row,
    reward: rewards[index]?.data,
    refreshing: rewards[index]?.isFetching ?? false,
    error: rewards[index]?.isError ?? false,
  }))

  useEffect(() => {
    scopeRef.current = scope
    setRequesting(null)
    setRecoveryRequired(null)
    inFlight.current = false
    return () => {
      scopeRef.current = ''
      void client.cancelQueries({ queryKey: ['qrPromo', tenant, userId] })
    }
  }, [client, tenant, userId])

  // Subscriptions update the existing balance/order/P&L queries. Coalesce their
  // bursts so each trading change refreshes live eligibility once.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return
      const key = JSON.stringify(event.query.queryKey)
      if (!/ledger\/balances|\/orders|\/pnl|\/pl\//i.test(key)) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['qrPromo', tenant, userId] })
      }, 750)
    })
    return () => {
      unsubscribe()
      if (timer) clearTimeout(timer)
    }
  }, [client, tenant, userId])

  const [pollEpoch, setPollEpoch] = useState(0)
  useEffect(() => {
    const resume = () => setPollEpoch((value) => value + 1)
    window.addEventListener('focus', resume)
    return () => window.removeEventListener('focus', resume)
  }, [])
  // Use phase changes to restart the finite poll budget without restarting on every refreshed response.
  const pollPhase = entries
    .map(
      (e) => `${e.challenge.id}:${e.result.status}:${e.reward?.state}:${e.reward?.kyc_completed}`,
    )
    .join('|')
  const needsPolling =
    enabled &&
    (participations.isError ||
      entries.some(
        (e) =>
          e.result.status === 'PENDING' ||
          !e.reward ||
          !['PAID', 'LAPSED'].includes(e.reward.state),
      ))
  useEffect(() => {
    if (!needsPolling) return
    const budget = createQrPollBudget()
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = () => {
      const delay = budget.nextDelay()
      if (stopped || delay === false) return
      timer = setTimeout(async () => {
        await Promise.all([
          client.refetchQueries({
            queryKey: ['qrPromo', tenant, userId, 'participants'],
            type: 'active',
          }),
          client.refetchQueries({
            queryKey: ['qrPromo', tenant, userId, 'reward'],
            type: 'active',
          }),
        ])
        schedule()
      }, delay)
    }
    schedule()
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }, [client, tenant, userId, needsPolling, pollPhase, pollEpoch])

  const settlementState = entries.map((e) => `${e.challenge.id}:${e.reward?.state}`).join('|')
  useEffect(() => {
    if (!settlementState || !/PENDING_KYC|PAID|LAPSED/.test(settlementState)) return
    void client.invalidateQueries({ queryKey: getUserSelfQueryKey() })
    void client.invalidateQueries({ queryKey: ['qrPromo', tenant, userId, 'participants'] })
    void client.invalidateQueries({
      predicate: (q) => /ledger\/balances|\/orders|\/pnl|\/pl\//i.test(JSON.stringify(q.queryKey)),
    })
  }, [client, settlementState, tenant, userId])

  const refresh = async (id?: string) => {
    if (!enabled) throw new Error('Sign in to refresh your challenge.')
    id = id ?? recoveryRequired ?? undefined
    const originalScope = scope
    const assertScope = () => {
      if (scopeRef.current !== originalScope)
        throw new Error('Your account changed. Reload this page.')
    }
    // QueryClient owns these reads, including cancellation. A pre-request poll
    // must never replace the newer snapshot returned after wind-down.
    await cancelQrRefreshes(client, ['qrPromo', tenant, userId], getUserSelfQueryKey())
    assertScope()
    const [, , freshReward] = await Promise.all([
      client.fetchQuery({
        queryKey: getUserSelfQueryKey(),
        staleTime: 0,
        retry: false,
        queryFn: async ({ signal }) => {
          const freshUser = await qrRequest<User>('/v1/user/self', { signal })
          assertScope()
          if (freshUser.id !== userId || freshUser.tenant_id !== tenant)
            throw new Error('Your account changed. Reload this page.')
          return { ...self, data: freshUser }
        },
      }),
      client.fetchQuery({
        queryKey: associationKey,
        staleTime: 0,
        retry: false,
        queryFn: async ({ signal }) => {
          const freshRows = await loadParticipations(tenant!, userId!, qrRequest, signal)
          assertScope()
          return freshRows
        },
      }),
      id
        ? client.fetchQuery({
            queryKey: rewardKey(id),
            staleTime: 0,
            retry: false,
            queryFn: async ({ signal }) => {
              const fresh = await qrRequest<QrReward>(
                `/v1/trading_challenges/${encodeURIComponent(id)}/reward`,
                { signal },
              )
              assertScope()
              return fresh
            },
          })
        : Promise.resolve(undefined),
    ])
    assertScope()
    if (!id) await client.invalidateQueries({ queryKey: ['qrPromo', tenant, userId, 'reward'] })
    setRecoveryRequired((current) => (current === id ? null : current))
    // Refresh the existing account balances/orders after wind-down and settlement.
    void client.invalidateQueries({
      predicate: (q) => /ledger\/balances|\/orders|\/pnl|\/pl\//i.test(JSON.stringify(q.queryKey)),
    })
    return freshReward
  }
  const requestReward = async (id: string) => {
    // The ref closes the gap before a render; uncertain prior claims require a status refresh first.
    if (inFlight.current || recoveryRequired || !enabled) return
    inFlight.current = true
    setRequesting(id)
    const originalScope = scope
    try {
      await requestQrReward({
        challengeId: id,
        userId: userId!,
        refresh: () => refresh(id),
        submit: () =>
          qrRequest<QrRewardAccepted>(`/v1/trading_challenges/${encodeURIComponent(id)}/claim`, {
            method: 'POST',
          }),
        isCurrent: () => scopeRef.current === originalScope,
        accept: async (preview, accepted) => {
          await cancelQrRefreshes(client, ['qrPromo', tenant, userId])
          if (scopeRef.current !== originalScope) return
          client.setQueryData(rewardKey(id), {
            ...preview,
            state: 'PENDING_KYC',
            reward_amount: accepted.reward_amount,
            reward_deadline_at: accepted.reward_deadline_at,
            kyc_url: accepted.kyc_url,
            blockers: ['ALREADY_REQUESTED'],
          } satisfies QrReward)
        },
        requireRecovery: () => setRecoveryRequired(id),
      })
    } finally {
      if (scopeRef.current === originalScope) {
        inFlight.current = false
        setRequesting(null)
      }
    }
  }
  const accountLocked = Boolean(user?.disabled_at || user?.closed_at)
  // Keep trading disabled during eligibility discovery, claim submission, and uncertain recovery.
  const tradingLocked =
    (enabled && participations.isPending) ||
    accountLocked ||
    Boolean(requesting) ||
    entries.some((entry) =>
      isQrTradingLocked(entry.result, entry.reward, requesting === entry.challenge.id),
    ) ||
    Boolean(recoveryRequired)
  return (
    <QrPromoContext.Provider
      value={{
        entries,
        loading: enabled && participations.isPending,
        error: participations.isError,
        tradingLocked,
        requesting,
        recoveryRequired,
        refresh,
        requestReward,
      }}
    >
      {children}
    </QrPromoContext.Provider>
  )
}
export function useQrPromo() {
  return useContext(QrPromoContext)
}
export function useQrTradingLocked() {
  return useQrPromo()?.tradingLocked ?? false
}
